/**
 * PushCampaignsPage.tsx
 *
 * Push notification campaign list, create flow, and stats view.
 * Editor is in PushCampaignEditorPage.tsx (routed at /marketing/push-campaigns/:id).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Clock, Bell, ArrowLeft, Eye, Search, AlertTriangle, CheckCircle, XCircle, FileText, Smartphone, RotateCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatCard } from '@/components/ui/StatCard';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { FormField } from '@/components/ui/FormField';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { FilterTabs } from '@/components/ui/FilterTabs';
import { MarketingHistoryCard } from '@/components/marketing/MarketingHistoryCard';
import { cn } from '@/lib/utils';
import type { BadgeVariant } from '@/types/common.types';
import { usePageContext } from '@/hooks/usePageContext';
import * as marketingSvc from '@/services/marketing.service';
import type { PushCampaign, PushCampaignContact, PushContactFilter } from '@/services/marketing.service';
// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
};


// ─── Helpers ────────────────────────────────────────────────────────────────

function fmtDate(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function fmtDateTime(d: string | null | undefined): string {
  if (!d) return '\u2014';
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ─── Views ──────────────────────────────────────────────────────────────────

type View =
  | { mode: 'list' }
  | { mode: 'create' }
  | { mode: 'stats'; id: number };

type NavTarget = View | { mode: 'edit'; id: number };

// ═══════════════════════════════════════════════════════════════════════════
// Root Component
// ═══════════════════════════════════════════════════════════════════════════

export default function PushCampaignsPage() {
  const [view, setView] = useState<View>({ mode: 'list' });
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Push Campaigns');

  const handleNavigate = useCallback((v: NavTarget) => {
    if (v.mode === 'edit') {
      navigate(`/marketing/push-campaigns/${v.id}`);
    } else {
      setView(v);
    }
  }, [navigate]);

  useEffect(() => {
    updateAIContext({
      filters: `View: ${view.mode}${view.mode === 'stats' ? `, Campaign ID: ${(view as any).id}` : ''}`,
      dataSummary: `Current view: ${view.mode}`,
    });
  }, [view, updateAIContext]);

  switch (view.mode) {
    case 'list':
      return <CampaignListView onNavigate={handleNavigate} />;
    case 'create':
      return <CampaignCreateView onNavigate={handleNavigate} />;
    case 'stats':
      return <CampaignStatsView id={view.id} onNavigate={handleNavigate} />;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 1) Campaign List View
// ═══════════════════════════════════════════════════════════════════════════

function CampaignListView({ onNavigate }: { onNavigate: (v: NavTarget) => void }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['marketing', 'push-campaigns', { search, status: statusFilter, page }],
    queryFn: () => marketingSvc.listPushCampaigns({ search, status: statusFilter || undefined, page, perPage: 25 }),
  });

  const { data: listStats } = useQuery({
    queryKey: ['marketing', 'push-campaigns', 'stats'],
    queryFn: () => marketingSvc.getPushCampaignListStats(),
    staleTime: 60_000,
  });

  const campaigns = data?.data ?? [];
  const meta = data?.meta;

  const draftCount     = listStats?.draft     ?? 0;
  const scheduledCount = listStats?.scheduled ?? 0;
  const sentCount      = listStats?.sent      ?? 0;

  const columns: DataTableColumn<PushCampaign>[] = [
    {
      key: 'name',
      header: 'Campaign',
      render: (r) => (
        <div>
          <span className="font-medium text-slate-900">{r.name}</span>
          {r.title && (
            <span className="block text-xs text-slate-500 truncate max-w-xs">{r.title}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'neutral'} dot>
          {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
        </Badge>
      ),
    },
    { key: 'totalRecipients', header: 'Recipients', render: (r) => (r.totalRecipients ?? 0).toLocaleString() },
    {
      key: 'sentAt',
      header: 'Scheduled / Sent',
      render: (r) => {
        if (r.sentAt) return <span className="text-sm text-slate-600">{fmtDate(r.sentAt)}</span>;
        if (r.scheduledAt) return <span className="text-sm text-amber-600">{fmtDate(r.scheduledAt)}</span>;
        return <span className="text-sm text-slate-400">{'\u2014'}</span>;
      },
    },
    { key: 'createDate', header: 'Created', render: (r) => fmtDate(r.createDate) },
  ];

  function handleRowClick(r: PushCampaign) {
    if (r.status === 'sent' || r.status === 'sending') {
      onNavigate({ mode: 'stats', id: r.pushCampaignId });
    } else {
      onNavigate({ mode: 'edit', id: r.pushCampaignId });
    }
  }

  const filterTabs = [
    { label: 'All', value: '' },
    { label: 'Draft', value: 'draft' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Sent', value: 'sent' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden p-6">
      <div className="flex-shrink-0 space-y-6">
        <PageHeader
          variant="compact"
          title="Push Campaigns"
          actions={(
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => onNavigate({ mode: 'create' })}>
              New Campaign
            </Button>
          )}
        />
        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total" value={listStats?.total ?? meta?.total ?? 0} icon={<Bell className="h-5 w-5 text-slate-400" />} />
          <StatCard label="Draft" value={draftCount} icon={<FileText className="h-5 w-5 text-slate-400" />} />
          <StatCard label="Scheduled" value={scheduledCount} icon={<Clock className="h-5 w-5 text-amber-400" />} />
          <StatCard label="Sent" value={sentCount} icon={<Send className="h-5 w-5 text-emerald-500" />} />
        </div>

        {/* Filter tabs + search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <FilterTabs
            tabs={filterTabs}
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
          />
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search campaigns..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 w-64"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 mt-6">
        <DataTable
          keyField="pushCampaignId"
          columns={columns}
          data={campaigns}
          loading={isLoading}
          page={meta?.page ?? 1}
          pageSize={meta?.perPage ?? 25}
          total={meta?.total ?? 0}
          onPageChange={setPage}
          onRowClick={handleRowClick}
          emptyTitle="No push campaigns yet"
          emptyDescription="Create your first push notification campaign to get started."
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 2) Campaign Create View
// ═══════════════════════════════════════════════════════════════════════════

function CampaignCreateView({ onNavigate }: { onNavigate: (v: NavTarget) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createMut = useMutation({
    mutationFn: (d: { name: string; description?: string }) => marketingSvc.createPushCampaign(d),
    onSuccess: (campaign) => {
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns'] });
      toast.success('Campaign created');
      onNavigate({ mode: 'edit', id: campaign.pushCampaignId });
    },
    onError: () => toast.error('Failed to create campaign'),
  });

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => onNavigate({ mode: 'list' })} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <h1 className="text-xl font-semibold text-slate-900">Create Push Campaign</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => onNavigate({ mode: 'list' })}>Cancel</Button>
          <Button
            loading={createMut.isPending}
            disabled={!name.trim()}
            onClick={() => createMut.mutate({ name: name.trim(), description: description.trim() || undefined })}
          >
            Next: Edit Campaign
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-violet-600" />
            <span className="font-semibold text-slate-800">Push Campaign Details</span>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <FormField label="Campaign Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Holiday Promo Notification"
            />
          </FormField>
          <FormField label="Description" helpText="For internal reference only -- not visible to recipients">
            <Textarea autoResize
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Internal notes about this campaign..."
            />
          </FormField>
        </CardBody>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// (Edit view extracted to PushCampaignEditorPage.tsx)
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// Push Notification Mockup
// ═══════════════════════════════════════════════════════════════════════════

function PushNotificationMockup({
  title,
  body,
  imageUrl,
  compact = false,
}: {
  title: string;
  body: string;
  imageUrl?: string;
  compact?: boolean;
}) {
  const width = compact ? 'w-[260px]' : 'w-[320px]';

  return (
    <div className={cn(width)}>
      {/* iOS-style notification card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
        {/* App header */}
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
          <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <Bell className="h-3 w-3 text-white" />
          </div>
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Next Health</span>
          <span className="ml-auto text-[10px] text-slate-400">now</span>
        </div>

        {/* Notification content */}
        <div className="p-3">
          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <p className={cn(
                'font-semibold text-slate-900 leading-snug',
                compact ? 'text-xs' : 'text-sm',
              )}>
                {title || 'Notification Title'}
              </p>
              <p className={cn(
                'text-slate-600 mt-0.5 leading-snug',
                compact ? 'text-[11px] line-clamp-3' : 'text-xs line-clamp-4',
              )}>
                {body || 'Your notification message will appear here...'}
              </p>
            </div>
            {imageUrl && (
              <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-slate-100">
                <img src={imageUrl} alt="Notification" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
        </div>

        {/* Large image (if provided and not compact) */}
        {imageUrl && !compact && (
          <div className="px-3 pb-3">
            <div className="rounded-lg overflow-hidden border border-slate-100">
              <img src={imageUrl} alt="Rich notification" className="w-full h-auto max-h-40 object-cover" />
            </div>
          </div>
        )}
      </div>

      {/* Lock screen context */}
      <div className="mt-3 text-center">
        <div className="inline-flex items-center gap-1 text-[10px] text-slate-400">
          <Smartphone className="h-3 w-3" />
          <span>Push Notification Preview</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// 4) Campaign Stats View
// ═══════════════════════════════════════════════════════════════════════════

// Maps a recipient status (or one of the bundled filters) to a Tailwind chip
// class. Used in the recipient table and the filter chip row.
const RECIPIENT_BADGE_CLASS: Record<string, string> = {
  queued: 'bg-slate-100 text-slate-600',
  sending: 'bg-amber-100 text-amber-800',
  sent: 'bg-blue-100 text-blue-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  opened: 'bg-violet-100 text-violet-800',
  clicked: 'bg-indigo-100 text-indigo-800',
  no_device: 'bg-slate-100 text-slate-500',
  unsubscribed: 'bg-slate-100 text-slate-500',
};

const FILTER_CHIPS: { value: '' | PushContactFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'engaged', label: 'Opened / Clicked' },
  { value: 'not_opened', label: 'Not opened' },
  { value: 'not_received', label: 'Did not receive' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_device', label: 'No device' },
];

function CampaignStatsView({ id, onNavigate }: { id: number; onNavigate: (v: NavTarget) => void }) {
  const [showPreview, setShowPreview] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'' | PushContactFilter>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  // Debounce search input so we don't refetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset pagination when filters change
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);

  // Poll campaign + stats + contacts while the campaign is in flight so the
  // status badge flips from "Sending" → "Sent" without a manual reload.
  // `isInFlight` is derived from campaign.status, so the campaign query has to
  // refetch too — otherwise the status is frozen at first load and polling
  // never stops.
  const { data: campaign, isLoading: campaignLoading } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id],
    queryFn: () => marketingSvc.getPushCampaign(id),
    refetchInterval: (q) => q.state.data?.status === 'sending' ? 5_000 : false,
  });

  const isInFlight = campaign?.status === 'sending';
  const { data: stats } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id, 'stats'],
    queryFn: () => marketingSvc.getPushCampaignStats(id),
    enabled: !!campaign,
    refetchInterval: isInFlight ? 5_000 : false,
  });

  const { data: contactsResp, isFetching: contactsFetching } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id, 'contacts', { page, statusFilter, search: debouncedSearch }],
    queryFn: () => marketingSvc.getPushCampaignContacts(id, {
      page,
      perPage: PER_PAGE,
      status: statusFilter || undefined,
      search: debouncedSearch || undefined,
    }),
    enabled: !!campaign,
    placeholderData: (prev) => prev,
    refetchInterval: isInFlight ? 5_000 : false,
  });

  const { data: campaignLists = [] } = useQuery({
    queryKey: ['marketing', 'push-campaigns', id, 'lists'],
    queryFn: () => marketingSvc.getPushCampaignLists(id),
    enabled: !!campaign,
  });

  const toast = useToast();
  const qc = useQueryClient();

  const resendMutation = useMutation({
    mutationFn: (recipientIds?: number[]) =>
      marketingSvc.resendFailedPushCampaign(id, recipientIds),
    onSuccess: (res) => {
      const parts: string[] = [];
      if (res.requeued > 0) parts.push(`${res.requeued} re-queued`);
      if (res.stillNoDevice > 0) parts.push(`${res.stillNoDevice} still without a device`);
      toast.success(parts.length ? parts.join(' · ') : 'Nothing to resend');
      qc.invalidateQueries({ queryKey: ['marketing', 'push-campaigns', id] });
      qc.invalidateQueries({ queryKey: ['marketing-history'] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : 'Resend failed'),
  });

  if (campaignLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16 text-slate-500">
        Campaign not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={() => onNavigate({ mode: 'list' })}>Back to Campaigns</Button>
        </div>
      </div>
    );
  }

  const totalSent       = stats?.totalSent ?? 0;
  const totalDelivered  = stats?.totalDelivered ?? 0;
  const totalFailed     = stats?.totalFailed ?? 0;
  const totalOpened     = stats?.totalOpened ?? 0;
  const totalClicked    = stats?.totalClicked ?? 0;
  const totalNoDevice   = stats?.totalNoDevice ?? 0;
  const totalRecipients = stats?.totalRecipients ?? campaign.totalRecipients ?? 0;
  const totalQueued     = stats?.totalQueued ?? 0;
  const totalSending    = stats?.totalSending ?? 0;

  const openRate = totalSent > 0 ? Math.round((totalOpened / totalSent) * 1000) / 10 : 0;

  const contacts: PushCampaignContact[] = contactsResp?.data ?? [];
  const contactsTotal = contactsResp?.meta?.total ?? 0;

  const STAT_CARDS: { label: string; value: number; tone: string; sub?: string }[] = [
    { label: 'Sent',      value: totalSent,      tone: 'text-violet-500' },
    { label: 'Delivered', value: totalDelivered, tone: 'text-emerald-500' },
    { label: 'Opened',    value: totalOpened,    tone: 'text-blue-500',  sub: openRate ? `${openRate}% open rate` : undefined },
    { label: 'Clicked',   value: totalClicked,   tone: 'text-indigo-500' },
    { label: 'Failed',    value: totalFailed,    tone: 'text-red-500' },
    { label: 'No Device', value: totalNoDevice,  tone: 'text-slate-500' },
  ];

  const recipientColumns: DataTableColumn<PushCampaignContact>[] = [
    {
      key: 'name',
      header: 'Patient',
      render: (r) => {
        const name = ((r.firstName ?? '') + ' ' + (r.lastName ?? '')).trim();
        return (
          <div>
            <div className="font-medium text-slate-800">{name || '\u2014'}</div>
            {r.email && <div className="text-xs text-slate-500 truncate max-w-[220px]">{r.email}</div>}
          </div>
        );
      },
    },
    {
      key: 'hasPushToken',
      header: 'Device',
      render: (r) => r.hasPushToken
        ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold"><CheckCircle className="h-3.5 w-3.5" /> Registered</span>
        : <span className="inline-flex items-center gap-1 text-slate-400 text-xs"><XCircle className="h-3.5 w-3.5" /> None</span>,
    },
    {
      key: 'deliveryStatus',
      header: 'Status',
      render: (r) => (
        <span className={cn(
          'inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold capitalize',
          RECIPIENT_BADGE_CLASS[r.deliveryStatus] ?? 'bg-slate-100 text-slate-600',
        )}>
          {r.deliveryStatus.replace('_', ' ')}
        </span>
      ),
    },
    {
      key: 'sentVia',
      header: 'Channel',
      render: (r) => r.sentVia ? <span className="text-xs uppercase text-slate-500">{r.sentVia}</span> : '\u2014',
    },
    {
      key: 'sentAt',
      header: 'Sent',
      render: (r) => <span className="text-xs text-slate-500">{r.sentAt ? fmtDate(r.sentAt) : '\u2014'}</span>,
    },
    {
      key: 'openedAt',
      header: 'Opened',
      render: (r) => <span className="text-xs text-slate-500">{r.openedAt ? fmtDate(r.openedAt) : '\u2014'}</span>,
    },
    {
      key: 'errorMessage',
      header: 'Reason',
      render: (r) => r.errorMessage
        ? (
          <span className="inline-flex items-center gap-1 text-red-500 text-xs" title={r.errorMessage}>
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate max-w-[180px]">{r.errorCode ?? r.errorMessage}</span>
          </span>
        )
        : '\u2014',
    },
    {
      key: 'resend',
      header: '',
      render: (r) => r.deliveryStatus === 'failed' ? (
        <button
          type="button"
          onClick={() => resendMutation.mutate([r.pushCampaignRecipientId])}
          disabled={resendMutation.isPending}
          title="Resend to this patient"
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-violet-600 hover:bg-violet-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RotateCw className={cn('h-3.5 w-3.5', resendMutation.isPending && 'animate-spin')} />
          Resend
        </button>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6 p-5">
      {/* Header */}
      <div className="flex items-start justify-between pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => onNavigate({ mode: 'list' })} className="p-1 rounded hover:bg-slate-100">
              <ArrowLeft className="h-5 w-5 text-slate-500" />
            </button>
            <h1 className="text-2xl font-semibold text-slate-800">{campaign.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={STATUS_BADGE[campaign.status] ?? 'neutral'} dot>
              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
            </Badge>
            <span className="text-sm text-slate-500">
              {campaign.sentAt
                ? `Sent on ${fmtDateTime(campaign.sentAt)}`
                : `Created ${fmtDateTime(campaign.createDate)}`}
            </span>
            {isInFlight && (totalQueued + totalSending) > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <Spinner className="h-3 w-3" />
                {(totalQueued + totalSending).toLocaleString()} in flight
              </span>
            )}
          </div>
          {campaign.description && (
            <p className="mt-2 text-sm text-slate-500">{campaign.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xl font-bold text-violet-500">{totalRecipients.toLocaleString()}</div>
            <div className="text-xs text-slate-500">{totalRecipients === 1 ? 'recipient' : 'recipients'}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
            <Eye className="h-4 w-4 mr-1" /> View Content
          </Button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="grid grid-cols-6 bg-white border border-slate-200 rounded-lg overflow-hidden">
        {STAT_CARDS.map((stat, i) => (
          <div
            key={stat.label}
            className={cn('py-5 px-4 text-center', i < STAT_CARDS.length - 1 && 'border-r border-slate-200')}
          >
            <div className={cn('text-3xl font-semibold mb-1', stat.value > 0 ? stat.tone : 'text-slate-400')}>
              {stat.value.toLocaleString()}
            </div>
            <div className="text-[11px] font-semibold uppercase text-slate-500 tracking-wider">
              {stat.label}
            </div>
            {stat.sub && <div className="text-[10px] text-slate-400 mt-0.5">{stat.sub}</div>}
          </div>
        ))}
      </div>

      {/* Campaign Details */}
      <Card>
        <CardHeader>Details</CardHeader>
        <CardBody className="p-0">
          <div className="grid grid-cols-[200px_1fr]">
            {[
              ['Campaign Name', campaign.name],
              campaign.title ? ['Notification Title', campaign.title] : null,
              campaign.description ? ['Description', campaign.description] : null,
              campaign.sentAt ? ['Sent At', fmtDateTime(campaign.sentAt)] : null,
              campaign.deepLink ? ['Deep Link', campaign.deepLink] : null,
            ]
              .filter(Boolean)
              .map((row, i) => (
                <React.Fragment key={i}>
                  <div className="px-5 py-3.5 font-semibold text-sm text-slate-700 bg-slate-50 border-b border-slate-100">
                    {(row as string[])[0]}
                  </div>
                  <div className="px-5 py-3.5 text-sm text-slate-600 border-b border-slate-100">
                    {(row as string[])[1]}
                  </div>
                </React.Fragment>
              ))}

            {campaignLists.length > 0 && (
              <>
                <div className="px-5 py-3.5 font-semibold text-sm text-slate-700 bg-slate-50">
                  Lists
                </div>
                <div className="px-5 py-3.5">
                  <div className="flex flex-wrap gap-2">
                    {campaignLists.map((li) => (
                      <span
                        key={li.emailListId}
                        className="inline-block px-2.5 py-1 bg-violet-50 text-violet-600 rounded text-sm"
                      >
                        {li.listName}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </CardBody>
      </Card>

      <MarketingHistoryCard type="push_campaign" id={id} />

      {/* Recipients */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <span>Recipients</span>
            <div className="flex items-center gap-3">
              {totalFailed > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resendMutation.mutate(undefined)}
                  disabled={resendMutation.isPending}
                  className="text-red-600 hover:bg-red-50"
                >
                  <RotateCw className={cn('h-4 w-4 mr-1', resendMutation.isPending && 'animate-spin')} />
                  Resend Failed ({totalFailed.toLocaleString()})
                </Button>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9 w-72"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-slate-100">
            {FILTER_CHIPS.map((chip) => {
              const count = chipCount(chip.value, {
                totalRecipients, totalSent, totalDelivered, totalOpened, totalClicked,
                totalFailed, totalNoDevice,
              });
              const active = statusFilter === chip.value;
              return (
                <button
                  key={chip.value || 'all'}
                  onClick={() => setStatusFilter(chip.value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors',
                    active
                      ? 'bg-violet-600 text-white border border-violet-600'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50',
                  )}
                >
                  {chip.label}
                  {count !== null && (
                    <span className={cn('text-[10px]', active ? 'text-violet-100' : 'text-slate-400')}>
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <DataTable<PushCampaignContact>
            data={contacts}
            columns={recipientColumns}
            keyField="pushCampaignRecipientId"
            page={page}
            pageSize={PER_PAGE}
            total={contactsTotal}
            onPageChange={setPage}
            loading={contactsFetching}
            emptyTitle={statusFilter || debouncedSearch ? 'No recipients match this filter' : 'No recipients yet'}
            emptyDescription={
              statusFilter || debouncedSearch
                ? 'Try clearing the filter or search to see other recipients.'
                : 'This campaign has not been sent yet.'
            }
          />
        </CardBody>
      </Card>

      {/* Content Preview Modal */}
      <Modal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Push Notification Preview" size="sm">
        <div className="flex justify-center py-4">
          <PushNotificationMockup
            title={campaign.title ?? ''}
            body={campaign.messageBody ?? ''}
            imageUrl={campaign.imageUrl ?? undefined}
          />
        </div>
      </Modal>
    </div>
  );
}

/**
 * Returns the headline count for a filter chip, or null when the chip should
 * not show a count (e.g. composite filters where the math is ambiguous).
 */
function chipCount(
  value: '' | PushContactFilter,
  totals: {
    totalRecipients: number;
    totalSent: number;
    totalDelivered: number;
    totalOpened: number;
    totalClicked: number;
    totalFailed: number;
    totalNoDevice: number;
  },
): number | null {
  switch (value) {
    case '':              return totals.totalRecipients;
    case 'engaged':       return totals.totalOpened + totals.totalClicked;
    case 'not_opened':    return Math.max(0, totals.totalSent - totals.totalOpened);
    case 'not_received':  return totals.totalFailed + totals.totalNoDevice;
    case 'failed':        return totals.totalFailed;
    case 'no_device':     return totals.totalNoDevice;
    default:              return null;
  }
}